import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Minus, Plus } from 'lucide-react';

export type EditLineData = {
  label: string;
  style: 'solid' | 'dashed' | 'dotted';
  thickness: number;
  color: string;
  price?: number;
};

export type EditLineModalProps = {
  initial: EditLineData;
  onSave: (data: EditLineData) => void;
  onCancel: () => void;
  showLabel?: boolean;
  showPrice?: boolean;
};

const presetColors = ['#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#FFFFFF', '#EC4899'];

export default function EditLineModal({ initial, onSave, onCancel, showLabel = true, showPrice = false }: EditLineModalProps) {
  const [editLineData, setEditLineData] = useState<EditLineData>({ ...initial });
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ active: boolean; offsetX: number; offsetY: number }>({
    active: false,
    offsetX: 0,
    offsetY: 0,
  });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        onSave(editLineData);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [editLineData, onCancel, onSave]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragStateRef.current.active) return;
      const modal = modalRef.current;
      if (!modal) return;

      const modalRect = modal.getBoundingClientRect();
      const nextX = e.clientX - dragStateRef.current.offsetX;
      const nextY = e.clientY - dragStateRef.current.offsetY;
      const maxX = Math.max(0, window.innerWidth - modalRect.width);
      const maxY = Math.max(0, window.innerHeight - modalRect.height);

      setPosition({
        x: Math.min(Math.max(0, nextX), maxX),
        y: Math.min(Math.max(0, nextY), maxY),
      });
    };

    const onUp = () => {
      dragStateRef.current.active = false;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const handleHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!modalRef.current) return;
    const rect = modalRef.current.getBoundingClientRect();
    dragStateRef.current = {
      active: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 p-4 bg-black/50">
      <div
        ref={modalRef}
        className="bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-2xl w-full max-w-[340px] shadow-2xl overflow-hidden flex flex-col absolute"
        style={position ? { left: `${position.x}px`, top: `${position.y}px` } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
      >
        {/* Header */}
        <div
          className="p-5 pb-4 flex items-center border-b border-zinc-800 cursor-move select-none"
          onPointerDown={handleHeaderPointerDown}
          title="Drag to move"
        >
          <h2 className="text-xl font-semibold text-zinc-100">Edit Line</h2>
        </div>
        <div className="p-6 flex flex-col gap-5">
          {showLabel && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-zinc-400">Label name</label>
              <input
                type="text"
                value={editLineData.label}
                onChange={(e) => setEditLineData({ ...editLineData, label: e.target.value })}
                className="bg-zinc-800 text-zinc-100 placeholder-zinc-500 border border-zinc-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
              />
            </div>
          )}
          {showPrice && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-zinc-400">Price</label>
              <input
                type="number"
                step="any"
                value={Number.isFinite(Number(editLineData.price)) ? Number(editLineData.price) : ''}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setEditLineData((prev) => ({
                    ...prev,
                    price: Number.isFinite(next) ? next : undefined,
                  }));
                }}
                className="bg-zinc-800 text-zinc-100 placeholder-zinc-500 border border-zinc-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                placeholder="Enter exact price"
              />
            </div>
          )}
          {/* Line Style */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-400">Line style</label>
            <div className="relative">
              <select
                value={editLineData.style}
                onChange={(e) => setEditLineData({ ...editLineData, style: e.target.value as any })}
                className="w-full bg-zinc-800 text-zinc-100 border border-zinc-700 rounded-lg pl-4 select-chevron-input py-2 appearance-none focus:ring-2 focus:ring-blue-500 focus:outline-none transition cursor-pointer"
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </select>
              <ChevronDown size={14} className="absolute select-chevron-icon top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
          </div>
          {/* Thickness */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-400">Line Thickness</label>
            <div className="flex items-center justify-between bg-zinc-800 border border-zinc-700 rounded-lg px-1 py-1 focus-within:ring-2 focus-within:ring-blue-500 transition">
              <button
                onClick={() => setEditLineData(prev => ({ ...prev, thickness: Math.max(1, prev.thickness - 1) }))}
                className="p-1.5 text-zinc-500 hover:text-zinc-100 hover:bg-zinc-700 rounded-md transition-colors"
              >
                <Minus size={14} strokeWidth={3} />
              </button>
              <input
                type="number"
                value={editLineData.thickness}
                onChange={(e) => setEditLineData(prev => ({ ...prev, thickness: parseInt(e.target.value) || 1 }))}
                className="bg-transparent border-none text-center w-16 text-sm text-zinc-100 focus:outline-none font-medium"
                style={{ MozAppearance: 'textfield', WebkitAppearance: 'none' }}
              />
              <button
                onClick={() => setEditLineData(prev => ({ ...prev, thickness: prev.thickness + 1 }))}
                className="p-1.5 text-zinc-500 hover:text-zinc-100 hover:bg-zinc-700 rounded-md transition-colors"
              >
                <Plus size={14} strokeWidth={3} />
              </button>
            </div>
          </div>
          {/* Color */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-zinc-400">Color</label>
            <div className="flex items-center justify-between gap-1.5">
              {presetColors.map(color => (
                <button
                  key={color}
                  onClick={() => setEditLineData(prev => ({ ...prev, color }))}
                  className={`w-8 h-8 rounded-[6px] transition-all duration-200 ${editLineData.color === color ? 'ring-2 ring-offset-2 ring-offset-zinc-900 ring-white scale-110 shadow-lg' : 'hover:scale-110 hover:ring-1 hover:ring-offset-1 hover:ring-offset-zinc-900 hover:ring-white/50 border border-black/20'}`}
                  style={{ backgroundColor: color }}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="p-5 border-t border-zinc-800 flex justify-center gap-3 bg-zinc-900">
          <button
            onClick={onCancel}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 transition-colors w-full"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(editLineData)}
            className="px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition duration-200 ease-in-out w-full"
          >
            Save
          </button>
        </div>
      </div>
      {/* hide native number input arrows */}
      <style dangerouslySetInnerHTML={{__html: `
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] { -moz-appearance: textfield; }
      `}} />
    </div>
  );
}
